import { Book, Laptop, Guitar, Music2, Package, Compass } from "lucide-react";
import { useNavigate } from "react-router-dom";
import notesWatercolor from "@/assets/notes-watercolor.png";

interface CourseCardProps {
  to: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
  featured?: boolean;
}

const CourseCard = ({ to, icon, title, description, onClick, featured }: CourseCardProps) => {
  const navigate = useNavigate();
  
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    onClick();
    navigate(to);
  };

  return (
    <a
      href={to}
      onClick={handleClick}
      className="block p-5 rounded-xl bg-gradient-to-br from-card to-muted/20 hover:from-accent/5 hover:to-primary/5 border border-border/50 transition-all duration-300 hover:scale-[1.02] hover:shadow-lg group"
    >
      <div className="flex items-start gap-4">
        <div className="p-3 rounded-lg bg-accent/10 text-accent group-hover:bg-accent group-hover:text-accent-foreground transition-all duration-300">
          {icon}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h3 className={`font-semibold text-foreground group-hover:text-accent transition-colors ${featured ? 'text-base' : 'text-sm'}`}>
              {title}
            </h3>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {description}
          </p>
        </div>
      </div>
    </a>
  );
};

interface KurseMegaMenuProps {
  onClose: () => void;
}

export const KurseMegaMenu = ({ onClose }: KurseMegaMenuProps) => {
  const navigate = useNavigate();

  const handleAllCoursesClick = (e: React.MouseEvent) => {
    e.preventDefault();
    onClose();
    navigate("/kurse");
  };

  return (
    <div className="w-[90vw] max-w-[800px] p-6">
      <div className="mb-4">
        <h2 className="text-2xl font-bold text-foreground mb-1">Kurse</h2>
        <p className="text-sm text-muted-foreground">
          Kreativpakete, Fortbildungen und mehr aus unserer Online-Akademie
        </p>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <CourseCard
          to="/kurse?kategorie=kreativpakete"
          icon={<Package className="w-5 h-5" />}
          title="Kreativpakete"
          description="Fertige Stundenbilder und Materialien"
          onClick={onClose}
        />
        
        <CourseCard
          to="/kurse?kategorie=online-fortbildungen"
          icon={<Laptop className="w-5 h-5" />}
          title="Online-Fortbildungen"
          description="Flexibel weiterbilden von zu Hause"
          onClick={onClose}
        />
        
        <CourseCard
          to="/kurse?kategorie=webinar-aufzeichnungen"
          icon={<Laptop className="w-5 h-5" />}
          title="Webinar-Aufzeichnungen"
          description="Aufgezeichnete Live-Sessions"
          onClick={onClose}
        />
        
        <CourseCard
          to="/kurse?kategorie=gitarre-ukulele"
          icon={<Guitar className="w-5 h-5" />}
          title="Gitarre & Ukulele"
          description="Spezialkurse für Saiteninstrumente"
          onClick={onClose}
        />
        
        <CourseCard
          to="/kurse?kategorie=liederwerkstatt"
          icon={<Music2 className="w-5 h-5" />}
          title="Liederwerkstatt"
          description="Gitarre, Singen & Ukulele - 14-tägige Live-Treffen"
          onClick={onClose}
        />
        
        <CourseCard
          to="/themenreisen"
          icon={<Compass className="w-5 h-5" />}
          title="Themenreisen"
          description="Kuratierte Lernpfade aus verschiedenen Kursen"
          onClick={onClose}
        />
      </div>
      
      <div className="mt-4 pt-4 border-t border-border/50">
        <a
          href="/kurse"
          onClick={handleAllCoursesClick}
          className="block p-6 rounded-xl bg-gradient-to-br from-primary/10 via-accent/10 to-primary/5 hover:from-primary/15 hover:via-accent/15 hover:to-primary/10 border-2 border-primary/20 hover:border-primary/40 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl group"
        >
          <div className="flex items-center gap-4">
            <div className="p-4 rounded-xl bg-primary/20 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-300 group-hover:scale-110">
              <Book className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-lg font-bold text-foreground group-hover:text-primary transition-colors">
                  Alle Kurse entdecken
                </h3>
              </div>
              <p className="text-sm text-muted-foreground">
                Gesamtübersicht aller verfügbaren Kurse und Angebote
              </p>
            </div>
          </div>
        </a>
      </div>
    </div>
  );
};
